import { useEffect, useRef, useState } from "react"
import { ArrowLeft } from "lucide-react"
import { toast } from 'sonner'
import { useUserRol, useTienePermiso} from '../App'
import { actualizarAceptado, guardarPrueba } from '../services/pruebasService'
import { RechazoModal } from "./RechazoModal"
import { Button } from "./ui/button"
import { Card, CardContent } from "./ui/card"
import { Input } from "./ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select"
import { Switch } from "./ui/switch"
import { Textarea } from "./ui/textarea"

interface FormData059_2 {
  ROW_ID?: string
  numero_solicitud: string
  fecha: string
  hora: string
  temperatura: string
  hr: string
  id_termohigrometro: string
  especimenes_u: string
  especimenes_t: string
  longitud_ensayo: string
  especimenes_rechazados: string
  estado_especimenes: string[]
  reportar: boolean
  tiene_metodo_muestreo: boolean
  metodo_muestreo: string
  no1_u: string; no2_u: string; no3_u: string; no4_u: string; no5_u: string
  no1_t: string; no2_t: string; no3_t: string; no4_t: string; no5_t: string
  promedio_u: string; redondeo_u: string
  promedio_t: string; redondeo_t: string
  observaciones: string
  mostrarObservaciones: boolean
  desv_metodo: string
  realizo: string
  superviso: string
}

interface Props {
  mode: "create" | "view" | "edit"
  initialData?: any
  onSave?: (data: any) => void
  onCancel?: () => void
  realizoUser?: string
  onRefreshBackend?: () => void
  rol?: string
  pedidoRowId?: string
  onAutosave?: (result: { rowId: string; nomId: string }) => void
}

function roundGrab(val: number): number {
  if (val < 100) return Math.round(val)
  if (val < 1000) return Math.round(val / 10) * 10
  return Math.round(val / 100) * 100
}

function calcPromedio(vals: string[]): string {
  const nums = vals.map(v => parseFloat(v)).filter(n => !isNaN(n))
  if (nums.length === 0) return ''
  return (nums.reduce((a, b) => a + b, 0) / nums.length).toFixed(3)
}

function calcDesv(vals: string[]): string {
  const nums = vals.map(v => parseFloat(v)).filter(n => !isNaN(n))
  if (nums.length < 2) return ''
  const mean = nums.reduce((a, b) => a + b, 0) / nums.length
  const variance = nums.reduce((sum, x) => sum + Math.pow(x - mean, 2), 0) / (nums.length - 1)
  return Math.sqrt(variance).toFixed(3)
}

function calcCV(prom: string, desv: string): string {
  const p = parseFloat(prom)
  const d = parseFloat(desv)
  if (!p || !d || isNaN(p) || isNaN(d)) return ''
  return ((d / p) * 100).toFixed(2)
}

const T_TABLE: Record<number, number> = {
  2: 12.706, 3: 4.303, 4: 3.182, 5: 2.776,
  6: 2.571, 7: 2.447, 8: 2.365, 9: 2.306, 10: 2.262,
}

function calcLC95(desv: string, vals: string[]): string {
  const d = parseFloat(desv)
  const n = vals.filter(v => v !== '' && !isNaN(parseFloat(v))).length
  if (!d || isNaN(d) || n < 2) return ''
  const t = T_TABLE[n] ?? 2.776
  return (t * d / Math.sqrt(n)).toFixed(3)
}

const DEFAULTS: FormData059_2 = {
  numero_solicitud: '', fecha: '', hora: '', temperatura: '', hr: '',
  id_termohigrometro: '', especimenes_u: '', especimenes_t: '',
  longitud_ensayo: '', especimenes_rechazados: '',
  estado_especimenes: [],
  reportar: true,
  tiene_metodo_muestreo: false,
  metodo_muestreo: '',
  no1_u: '', no2_u: '', no3_u: '', no4_u: '', no5_u: '',
  no1_t: '', no2_t: '', no3_t: '', no4_t: '', no5_t: '',
  promedio_u: '', redondeo_u: '',
  promedio_t: '', redondeo_t: '',
  observaciones: '', mostrarObservaciones: false, desv_metodo: '', realizo: '', superviso: '',
}

const ESTADOS = ['Acondicionados', 'Húmedos']
const TD = "border border-gray-500 px-1 py-0.5 align-top text-xs"

export function NMXA059_2Form({ mode, initialData = {}, onSave, onCancel, realizoUser, onRefreshBackend, rol, pedidoRowId, onAutosave }: Props) {
  const globalRol = useUserRol()
  const puedeRevisar = useTienePermiso('aprobar_rechazar_resultado');
  const effectiveRol = rol || globalRol

  const parseEstado = (raw: any): string[] => {
    if (Array.isArray(raw)) return raw
    if (typeof raw === 'string' && raw) {
      try { return JSON.parse(raw) } catch { return raw.split(',').map((s: string) => s.trim()).filter(Boolean) }
    }
    return []
  }

  const [formData, setFormData] = useState<FormData059_2>({
    ...DEFAULTS,
    realizo: mode === 'create' && realizoUser ? realizoUser : '',
    ...initialData,
    estado_especimenes: parseEstado(initialData?.estado_especimenes),
    mostrarObservaciones: initialData?.MOSTRAR_OBSERVACIONES === true,
  })
  const [editMode, setEditMode] = useState(false)
  const [showRechazoModal, setShowRechazoModal] = useState(false)
  const [rechazoLoading, setRechazoLoading] = useState(false)

  const initialDataSyncedRef = useRef(false)
  useEffect(() => {
    if (initialDataSyncedRef.current) return
    initialDataSyncedRef.current = true
    setFormData({
      ...DEFAULTS,
      realizo: realizoUser || '',
      ...initialData,
      estado_especimenes: parseEstado(initialData?.estado_especimenes),
      mostrarObservaciones: initialData?.MOSTRAR_OBSERVACIONES === true,
    })
    setEditMode(false)
  }, [initialData])

  const aceptadoRaw = initialData?.ACEPTADO ?? initialData?.aceptado
  const aceptadoValue = typeof aceptadoRaw === 'string' ? aceptadoRaw.toLowerCase().trim() : ''
  const isAceptado = aceptadoValue === 'aceptado' || aceptadoValue === 'rechazado'
  const isReadOnly = mode === 'view' && !editMode
  const [autosaveRowId, setAutosaveRowId] = useState<string>(formData?.ROW_ID || initialData?.ROW_ID || '')
  const [autosaveNomId, setAutosaveNomId] = useState<string>(initialData?.nomId || '')
  const rowId = autosaveRowId
  const nomId = autosaveNomId

  const urdVals = [formData.no1_u, formData.no2_u, formData.no3_u, formData.no4_u, formData.no5_u]
  const traVals = [formData.no1_t, formData.no2_t, formData.no3_t, formData.no4_t, formData.no5_t]
  const desv_u = calcDesv(urdVals)
  const desv_t = calcDesv(traVals)
  const cv_u = calcCV(formData.promedio_u, desv_u)
  const cv_t = calcCV(formData.promedio_t, desv_t)
  const lc95_u = calcLC95(desv_u, urdVals)
  const lc95_t = calcLC95(desv_t, traVals)

  const set = (field: keyof FormData059_2, value: any) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value }
      const uv = [next.no1_u, next.no2_u, next.no3_u, next.no4_u, next.no5_u]
      const tv = [next.no1_t, next.no2_t, next.no3_t, next.no4_t, next.no5_t]
      const promU = calcPromedio(uv)
      const promT = calcPromedio(tv)
      next.promedio_u = promU
      next.promedio_t = promT
      if (promU) { next.redondeo_u = String(roundGrab(parseFloat(promU))) }
      if (promT) { next.redondeo_t = String(roundGrab(parseFloat(promT))) }
      return next
    })
  }

  const toggleEstado = (estado: string) => {
    if (isReadOnly) return
    setFormData(prev => {
      const arr = prev.estado_especimenes
      return { ...prev, estado_especimenes: arr.includes(estado) ? arr.filter(e => e !== estado) : [...arr, estado] }
    })
  }

  const formPayload = () => {
    const { ROW_ID, ...rest } = formData
    return {
      ...rest,
      estado_especimenes: formData.estado_especimenes.join(', '),
    }
  }

  const [guardando, setGuardando] = useState(false)

  const handleSave = async () => {
    if (guardando) return
    setGuardando(true)
    try {
      // Cancela cualquier autoguardado pendiente y espera a que termine el que ya
      // esté en curso, para que el guardado explícito sea siempre el último en
      // escribir (si no, un autoguardado tardío puede revertir esto a "borrador").
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) {
        await new Promise(r => setTimeout(r, 100))
      }
      if (onSave) onSave(formPayload())
    } finally {
      setGuardando(false)
    }
  }

  const handleGuardarEdicion = () => {
    if (!window.confirm('¿Seguro que quieres guardar y aceptar la edición de NMX-A-059-2? Esta acción no se puede deshacer.')) return
    if (onSave) onSave({
      ...formPayload(),
      ROW_ID: rowId,
      _EDIT_MODE: true,
    } as any)
  }

  // ── Autosave (borrador) ──────────────────────────────────────────────────
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isFirstAutosaveRenderRef = useRef(true)
  const autosaveInFlightRef = useRef(false)
  const rowIdRef = useRef(rowId)
  const nomIdRef = useRef(nomId)
  useEffect(() => { rowIdRef.current = rowId }, [rowId])
  useEffect(() => { nomIdRef.current = nomId }, [nomId])

  const doAutosave = async () => {
    if (autosaveInFlightRef.current) return
    autosaveInFlightRef.current = true
    try {
      const { pruebaId, nomId: savedNomId } = await guardarPrueba(
        'Propiedades de los tejidos frente a la tracción-Parte 2: Determinación de la fuerza máxima por el método de agarre-Método Grab. NMX-059/2-INNTEX-2019',
        pedidoRowId || '',
        formPayload(),
        nomIdRef.current || undefined,
        rowIdRef.current || undefined,
        true,
      )
      setAutosaveRowId(String(pruebaId))
      setAutosaveNomId(savedNomId)
      rowIdRef.current = String(pruebaId)
      nomIdRef.current = savedNomId
      onAutosave?.({ rowId: String(pruebaId), nomId: savedNomId })
    } catch (err) {
      console.warn('Autosave de borrador falló (silencioso):', err)
    } finally {
      autosaveInFlightRef.current = false
    }
  }

  useEffect(() => {
    if (isFirstAutosaveRenderRef.current) {
      isFirstAutosaveRenderRef.current = false
      return
    }
    if (mode === 'view' || isAceptado) return

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null
      doAutosave()
    }, 1500)

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, mode, isAceptado])

  // Si hay un autoguardado pendiente (debounce de 1.5s) y el usuario navega hacia
  // atrás antes de que se dispare, se cancelaba sin guardarse nunca. Al salir,
  // se ejecuta de inmediato ese guardado pendiente con los datos más recientes.
  const handleBackFlush = () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current)
      autosaveTimerRef.current = null
      if (mode !== 'view' && !isAceptado) {
        doAutosave()
      }
    }
    onCancel?.()
  }

  const handleAccion = async () => {
    if (!window.confirm('Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres aceptar la prueba NMX-A-059-2?')) return
    try {
      // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
      // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
      await actualizarAceptado(rowId, 'aceptado', undefined, undefined, nomId || undefined)
      toast.success('Prueba aceptada correctamente.')
      onRefreshBackend?.()
    } catch (e: any) { toast.error(e.message) }
  }

  const inputCls = (extra?: string) =>
    `h-6 text-xs px-1 ${isReadOnly ? 'bg-transparent border-0 shadow-none cursor-not-allowed' : ''} ${extra ?? ''}`

  return (
    <>
      {onCancel && (
        <Button variant="ghost" size="sm" onClick={handleBackFlush}>
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Button>
      )}
      <Card className="w-full max-w-4xl mx-auto">
      <CardContent className="p-4 space-y-3">

        {/* ── HEADER TABLE ── */}
        <table className="w-full text-xs border-collapse border-2 border-gray-700">
          <tbody>
            {/* Row 1: Número de solicitud */}
            <tr>
              <td colSpan={5} className={TD}>
                <div className="flex items-center gap-2">
                  <span className="font-bold whitespace-nowrap">NÚMERO DE SOLICITUD:</span>
                  <Input className={inputCls('w-48')}
                    value={formData.numero_solicitud} readOnly={isReadOnly}
                    onChange={e => set('numero_solicitud', e.target.value)} />
                </div>
              </td>
            </tr>

            {/* Row 2: Título + MET */}
            <tr>
              <td colSpan={4} className={`${TD} text-center font-bold leading-snug py-1`}>
                Propiedades de los tejidos frente a la tracción-Parte 2: Determinación de la fuerza máxima por el método de agarre-Método Grab. NMX-059/2-INNTEX-2019
              </td>
              <td className={`${TD} text-center font-bold whitespace-nowrap`}>MET-7.2-20</td>
            </tr>

            {/* Row 3: Fecha / Hora / °C / %H.R. / ID Termohig */}
            <tr>
              <td className={TD}>
                <div className="font-medium whitespace-nowrap">Fecha:</div>
                <Input type="date" className={inputCls('w-full')}
                  value={formData.fecha} readOnly={isReadOnly}
                  onChange={e => set('fecha', e.target.value)} />
              </td>
              <td className={TD}>
                <div className="font-medium whitespace-nowrap">Hora:</div>
                <Input type="time" className={inputCls('w-full')}
                  value={formData.hora} readOnly={isReadOnly}
                  onChange={e => { if (e.target.value) set('hora', e.target.value) }} />
              </td>
              <td className={TD}>
                <div className="font-medium whitespace-nowrap">°C:</div>
                <Input type="number" className={inputCls('w-full')}
                  value={formData.temperatura} readOnly={isReadOnly}
                  onChange={e => set('temperatura', e.target.value)} />
              </td>
              <td className={TD}>
                <div className="font-medium whitespace-nowrap">%H.R.:</div>
                <Input type="number" className={inputCls('w-full')}
                  value={formData.hr} readOnly={isReadOnly}
                  onChange={e => set('hr', e.target.value)} />
              </td>
              <td className={TD}>
                <div className="font-medium whitespace-nowrap">ID de termohigrómetro:</div>
                {isReadOnly ? (
                  <Input value={formData.id_termohigrometro} readOnly className={inputCls('w-full')} />
                ) : (
                  <Select value={formData.id_termohigrometro} onValueChange={v => set('id_termohigrometro', v)}>
                    <SelectTrigger className="h-6 text-xs"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="E-59">E-59</SelectItem>
                      <SelectItem value="E-57">E-57</SelectItem>
                      <SelectItem value="E-56">E-56</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </td>
            </tr>

            {/* Row 4: Especímenes U y T / Longitud / Rechazados / Reportar */}
            <tr>
              <td colSpan={2} className={TD}>
                <div className="font-medium whitespace-nowrap mb-0.5">Especímenes ensayados:</div>
                <div className="flex items-center gap-1 flex-wrap">
                  <Input type="number" className={inputCls('w-12')}
                    value={formData.especimenes_u} readOnly={isReadOnly}
                    onChange={e => set('especimenes_u', e.target.value)} />
                  <span>en U y</span>
                  <Input type="number" className={inputCls('w-12')}
                    value={formData.especimenes_t} readOnly={isReadOnly}
                    onChange={e => set('especimenes_t', e.target.value)} />
                  <span>en T.</span>
                </div>
                <div className="flex items-center gap-1.5 mt-1">
                  <input type="checkbox" disabled={isReadOnly}
                    checked={formData.reportar !== false}
                    onChange={e => set('reportar', e.target.checked)} />
                  <span className="text-xs">Se reporta %CV</span>
                </div>
                <div className="flex items-center gap-1.5 mt-1">
                  <input type="checkbox" disabled={isReadOnly}
                    checked={!!formData.tiene_metodo_muestreo}
                    onChange={e => set('tiene_metodo_muestreo', e.target.checked)} />
                  <span className="text-xs">Método de toma de muestreo</span>
                </div>
                {formData.tiene_metodo_muestreo && (
                  <input
                    type="text"
                    disabled={isReadOnly}
                    placeholder="Describir método..."
                    value={formData.metodo_muestreo}
                    onChange={e => set('metodo_muestreo', e.target.value)}
                    className={`mt-1 w-full h-6 text-xs px-1 border border-gray-300 rounded ${isReadOnly ? 'bg-transparent border-0 shadow-none cursor-not-allowed' : ''}`}
                  />
                )}
              </td>
              <td colSpan={2} className={TD}>
                <div className="font-medium whitespace-nowrap">Longitud del ensayo:</div>
                <div className="flex items-center gap-1">
                  <Input type="number" className={inputCls('w-full')}
                    value={formData.longitud_ensayo} readOnly={isReadOnly}
                    onChange={e => set('longitud_ensayo', e.target.value)} />
                  <span className="whitespace-nowrap">mm</span>
                </div>
              </td>
              <td className={TD}>
                <div className="font-medium whitespace-nowrap">Especímenes rechazados:</div>
                <Input type="number" className={inputCls('w-full')}
                  value={formData.especimenes_rechazados} readOnly={isReadOnly}
                  onChange={e => set('especimenes_rechazados', e.target.value)} />
              </td>
            </tr>

            {/* Row 5: Estado de los especímenes */}
            <tr>
              <td className={`${TD} whitespace-nowrap`}>
                <span className="font-medium">Estado de los especímenes:</span>
              </td>
              <td colSpan={4} className={TD}>
                <div className="flex items-center gap-4">
                  {ESTADOS.map(est => (
                    <label key={est} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" disabled={isReadOnly}
                        checked={formData.estado_especimenes.includes(est)}
                        onChange={() => toggleEstado(est)} />
                      {est}
                    </label>
                  ))}
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        {/* ── MAIN SECTION: tabla datos (izq) + Realizó/Supervisó (der) ── */}
        <table className="w-full text-xs border-collapse border border-gray-500">
          <tbody>
            <tr>
              {/* Left: measurement table */}
              <td className="border border-gray-500 p-2 align-top w-[55%]">
                <table className="w-full text-xs border-collapse border border-gray-500">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-500 p-1 text-center w-14">No.</th>
                      <th className="border border-gray-500 p-1 text-center">Urdimbre</th>
                      <th className="border border-gray-500 p-1 text-center">Trama</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[1,2,3,4,5].map(i => (
                      <tr key={i}>
                        <td className="border border-gray-500 p-1 text-center">{i}</td>
                        <td className="border border-gray-500 p-0.5">
                          <Input className="h-5 text-xs px-1 border-0 shadow-none"
                            value={(formData as any)[`no${i}_u`] || ''} readOnly={isReadOnly}
                            onChange={e => set(`no${i}_u` as keyof FormData059_2, e.target.value)} />
                        </td>
                        <td className="border border-gray-500 p-0.5">
                          <Input className="h-5 text-xs px-1 border-0 shadow-none"
                            value={(formData as any)[`no${i}_t`] || ''} readOnly={isReadOnly}
                            onChange={e => set(`no${i}_t` as keyof FormData059_2, e.target.value)} />
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50 font-semibold">
                      <td className="border border-gray-500 p-1">Promedio</td>
                      <td className="border border-gray-500 p-1 text-center">{formData.promedio_u}</td>
                      <td className="border border-gray-500 p-1 text-center">{formData.promedio_t}</td>
                    </tr>
                    <tr className="bg-gray-50 font-semibold">
                      <td className="border border-gray-500 p-1">Redondeo</td>
                      <td className="border border-gray-500 p-1 text-center">
                        {formData.redondeo_u ? `${formData.redondeo_u} N` : 'N'}
                      </td>
                      <td className="border border-gray-500 p-1 text-center">
                        {formData.redondeo_t ? `${formData.redondeo_t} N` : 'N'}
                      </td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-500 p-1 font-semibold">σ</td>
                      <td className="border border-gray-500 p-1 text-center">{desv_u}</td>
                      <td className="border border-gray-500 p-1 text-center">{desv_t}</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-500 p-1 font-semibold">CV (%)</td>
                      <td className="border border-gray-500 p-1 text-center">{cv_u}</td>
                      <td className="border border-gray-500 p-1 text-center">{cv_t}</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-500 p-1 font-semibold whitespace-nowrap">LC 95%</td>
                      <td className="border border-gray-500 p-1 text-center">{lc95_u}</td>
                      <td className="border border-gray-500 p-1 text-center">{lc95_t}</td>
                    </tr>
                  </tbody>
                </table>
              </td>

              {/* Right: Realizó / Supervisó */}
              <td className="border border-gray-500 p-2 align-top w-[45%]">
                <div className="space-y-4">
                  <div>
                    <div className="text-xs font-medium mb-1">Realizó:</div>
                    <Input value={formData.realizo} readOnly
                      className="h-6 text-xs border-0 shadow-none bg-transparent cursor-not-allowed" />
                  </div>
                  <div>
                    <div className="text-xs font-medium mb-1">Supervisó:</div>
                    <Input value={formData.superviso} readOnly
                      className="h-6 text-xs border-0 shadow-none bg-transparent cursor-not-allowed" />
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        {/* ── FOOTER ── */}
        <table className="w-full text-xs border-collapse border border-gray-500">
          <tbody>
            <tr>
              <td className="border border-gray-500 px-2 py-1">
                <div className="font-medium mb-1 flex items-center gap-2">
                  Observaciones:
                  <span className="ml-auto flex items-center gap-1.5 text-xs font-normal text-muted-foreground">
                    Mostrar en Reporte Cliente
                    <Switch checked={formData.mostrarObservaciones === true} disabled={isReadOnly}
                      onCheckedChange={(checked) => set('mostrarObservaciones', checked)} />
                  </span>
                </div>
                <Textarea value={formData.observaciones} readOnly={isReadOnly} rows={2}
                  className={`text-xs ${isReadOnly ? 'border-0 shadow-none bg-transparent resize-none' : ''}`}
                  onChange={e => set('observaciones', e.target.value)} />
              </td>
            </tr>
          </tbody>
        </table>

        {/* Desviación al método */}
        <div>
          <label className="text-xs font-medium block mb-1">Desviación al método</label>
          <Textarea value={formData.desv_metodo} readOnly={isReadOnly} rows={2}
            className={`text-xs ${isReadOnly ? 'bg-muted-foreground/10 cursor-not-allowed' : ''}`}
            onChange={e => set('desv_metodo', e.target.value)} />
        </div>

        <div className="flex gap-2 justify-end">
          {onCancel && <Button variant="outline" onClick={handleBackFlush}>Cancelar</Button>}
          {mode === 'view' && !editMode && !isAceptado && puedeRevisar && (
            <>
              <Button variant="default" onClick={handleAccion}>Aceptar</Button>
              <Button variant="destructive" onClick={() => setShowRechazoModal(true)}>Rechazar</Button>
              <Button variant="secondary" onClick={() => setEditMode(true)}>Editar</Button>
            </>
          )}
          {mode === 'view' && editMode && !isAceptado && puedeRevisar && (
            <Button variant="default" onClick={handleGuardarEdicion}>Guardar Edición</Button>
          )}
          {mode === 'create' && (
            <Button onClick={handleSave} disabled={guardando}>{guardando ? 'Guardando...' : 'Guardar Formulario'}</Button>
          )}
        </div>

      </CardContent>

      <RechazoModal
        open={showRechazoModal}
        onClose={() => setShowRechazoModal(false)}
        loading={rechazoLoading}
        onSubmit={async (razon: string) => {
          setRechazoLoading(true)
          try {
            // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
            // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
            if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
            while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
            await actualizarAceptado(rowId, 'rechazado', razon, undefined, nomId || undefined)
            toast.success('Prueba rechazada')
            setShowRechazoModal(false)
            onRefreshBackend?.()
          } catch (e: any) { toast.error(e.message) }
          finally { setRechazoLoading(false) }
        }}
      />
    </Card>
    </>
  )
}
